/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';

import * as ConstantAPI from 'ConstantAPI';
import { I18NError } from 'Utils';
import { fetchGet } from 'FetchAPIs';

const messages = defineMessages({
  errorContants: 'Error fetching constants.',
  errorRemove: 'Error removing a constant value',
  errorUpdate: 'Error updating a constant value',
});

const baseConstantURL = '/api/constants';

export const GET_CONSTANTS = 'GET_CONSTANTS';
export const GET_CONSTANTS_FAILURE = 'GET_CONSTANTS_FAILURE';
export const GET_CONSTANTS_SUCCESS = 'GET_CONSTANTS_SUCCESS';

export const GET_GLOBAL_CONSTANTS = 'GET_GLOBAL_CONSTANTS';
export const GET_GLOBAL_CONSTANTS_FAILURE = 'GET_GLOBAL_CONSTANTS_FAILURE';
export const GET_GLOBAL_CONSTANTS_SUCCESS = 'GET_GLOBAL_CONSTANTS_SUCCESS';
export const UPDATE_GLOBAL_CONSTANT_SUCCESS = 'UPDATE_GLOBAL_CONSTANT_SUCCESS';

export const ADD_CONSTANT = 'ADD_CONSTANT';
export const ADD_CONSTANT_FAILURE = 'ADD_CONSTANT_FAILURE';
export const ADD_CONSTANT_SUCCESS = 'ADD_CONSTANT_SUCCESS';

export const REMOVE_CONSTANT = 'REMOVE_CONSTANT';
export const REMOVE_CONSTANT_FAILURE = 'REMOVE_CONSTANT_FAILURE';
export const REMOVE_CONSTANT_SUCCESS = 'REMOVE_CONSTANT_SUCCESS';

export const UPDATE_CONSTANT = 'UPDATE_CONSTANT';
export const UPDATE_CONSTANT_FAILURE = 'UPDATE_CONSTANT_FAILURE';
export const UPDATE_CONSTANT_SUCCESS = 'UPDATE_CONSTANT_SUCCESS';

/**
 * ------------------
 * Get Constants
 * ------------------
 */

// action
const getConstants = () => ({
  type: GET_CONSTANTS,
});

// action
const getConstantsFailure = error => ({
  type: GET_CONSTANTS_FAILURE,
  payload: error,
});

// action
const getConstantsSuccess = (id, constants) => ({
  type: GET_CONSTANTS_SUCCESS,
  id,
  payload: constants,
});

// get
export const fetchConstants = entityType => dispatch => {
  dispatch(getConstants());
  return ConstantAPI.getConstants(entityType)
    .then(responseJson => {
      dispatch(getConstantsSuccess(entityType, responseJson || ''));
      return responseJson;
    })
    .catch(error => {
      dispatch(getConstantsFailure(error));
    });
};

/**
 * ------------------
 * Get Global Constants
 * ------------------
 */

// action
const getGlobalConstants = () => ({
  type: GET_GLOBAL_CONSTANTS,
});

// action
const getGlobalConstantsFailure = error => ({
  type: GET_GLOBAL_CONSTANTS_FAILURE,
  payload: error,
});

// action
const getGlobalConstantsSuccess = constants => ({
  type: GET_GLOBAL_CONSTANTS_SUCCESS,
  payload: constants,
});

// action
const updateGlobalConstantSuccess = storeData => ({
  type: UPDATE_GLOBAL_CONSTANT_SUCCESS,
  payload: storeData,
});

// get
export const fetchGlobalConstants = () => dispatch => {
  dispatch(getGlobalConstants());
  return ConstantAPI.getGlobalConstants()
    .then(responseJson => {
      dispatch(getGlobalConstantsSuccess(responseJson || ''));
      return responseJson;
    })
    .catch(error => {
      dispatch(getGlobalConstantsFailure(error));
    });
};

/**
 * ------------------
 * Add Constant
 * ------------------
 */

// action
/*
const startAddConstant = () => ({
  type: ADD_CONSTANT,
});

// action
const addConstantFailure = error => ({
  type: ADD_CONSTANT_FAILURE,
  payload: error,
});

// action
const addConstantSuccess = storeData => ({
  type: ADD_CONSTANT_SUCCESS,
  payload: storeData,
});

// add
export const addConstant = entityType => dispatch => {
  dispatch(startAddConstant());
  return ConstantAPI.addConstant(entityType)
    .then(responseJson => {
      dispatch(addConstantSuccess(responseJson || {}));
      return responseJson;
    })
    .catch(error => {
      dispatch(addConstantFailure(error));
      throw error;
    });
};
*/
/**
 * ------------------
 * Remove Constant
 * ------------------
 */

// action
const startRemoveConstant = () => ({
  type: REMOVE_CONSTANT,
});

// action
const removeConstantFailure = error => ({
  type: REMOVE_CONSTANT_FAILURE,
  payload: error,
});

// action
const removeConstantSuccess = storeData => ({
  type: REMOVE_CONSTANT_SUCCESS,
  payload: storeData,
});

// remove
export const removeConstant =
  (constant, entityType, globalDelete = false, globalDeleteOnly = false) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startRemoveConstant());
    const userEmailId = dataCache.get('currentUser')?.id;

    return ConstantAPI.removeConstant(constant, globalDelete, userEmailId, globalDeleteOnly)
      .then(() => {
        fetchGet(baseConstantURL, null, { entityType })
          .then(response => {
            dispatch(removeConstantSuccess(response || {}));
            return response;
          })
          .catch(error => {
            throw new I18NError(messages.errorContants, error);
          });
      })
      .catch(error => {
        dispatch(removeConstantFailure(error));
        throw error;
      });
  };

/**
 * ------------------
 * Update Constant
 * ------------------
 */

// action
const startupdateConstant = () => ({
  type: UPDATE_CONSTANT,
});

// action
const updateConstantFailure = error => ({
  type: UPDATE_CONSTANT_FAILURE,
  payload: error,
});

// action
const updateConstantSuccess = storeData => ({
  type: UPDATE_CONSTANT_SUCCESS,
  payload: storeData,
});

// update
export const updateConstant =
  (value, entityType, globalConst, constantList) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startupdateConstant());
    const userEmailId = dataCache.get('currentUser')?.id;

    return ConstantAPI.updateConstant(value, entityType, globalConst, constantList, userEmailId)
      .then(() => {
        fetchGet(baseConstantURL, null, { entityType })
          .then(response => {
            dispatch(updateConstantSuccess(response || {}));
            if (globalConst) {
              dispatch(updateGlobalConstantSuccess(response || {}));
            }
            return response;
          })
          .catch(error => {
            throw new I18NError(messages.errorContants, error);
          });
      })
      .catch(error => {
        dispatch(updateConstantFailure(error));
        throw error;
      });
  };
